import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button, Card, CardBody, Collapse, Progress } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCalendarAlt,
  faCheck,
  faChevronDown,
  faFileExcel,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import FalconCardHeader from "../common/FalconCardHeader";
import ButtonIcon from "../common/ButtonIcon";
import AliquotHealthAlert from "./AliquotHealthAlert";
import InitialLoadModal from "../properties/InitialLoadModal";
import InitialLoadSpotlight from "./InitialLoadSpotlight";
import { ONBOARDING_WELCOME_STEP } from "./onboardingSteps";
import useOnboardingProgress from "../../helpers/hooks/useOnboardingProgress";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";

const noop = () => {};

/**
 * HO-HU-36: guia de primeros pasos que ve el manager en el Inicio.
 *
 * No bloquea nada: es una tarjeta mas arriba del listado de condominios, para
 * que quien ya sabe lo que hace pueda ignorarla, y para que un condominio que
 * ya opera no se vea empujado a "empezar de nuevo" (sus pasos aparecen
 * detectados). La guia permanece visible incluso cuando todos estan completos.
 */
const OnboardingChecklist = ({ spotlightInitialLoad, onSpotlightClose }) => {
  const history = useHistory();
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const {
    enabled,
    isLoading,
    error,
    steps,
    currentStep,
    completed,
    total,
    isHidden,
    aliquot,
    markDone,
    setHidden,
    refresh,
  } = useOnboardingProgress();

  // La bienvenida arranca abierta: es lo primero que tiene que leer quien
  // entra al Inicio por primera vez.
  const [openKey, setOpenKey] = useState(ONBOARDING_WELCOME_STEP.key);
  const [busyKey, setBusyKey] = useState(null);
  const [hiding, setHiding] = useState(false);
  const [initialLoadOpen, setInitialLoadOpen] = useState(false);
  const welcomeRef = useRef(null);

  // El resaltado apunta al texto y a los botones de la bienvenida, asi que
  // tiene que estar desplegada aunque el usuario la haya cerrado.
  useEffect(() => {
    if (spotlightInitialLoad) {
      setOpenKey(ONBOARDING_WELCOME_STEP.key);
    }
  }, [spotlightInitialLoad]);

  const startInitialLoad = useCallback(() => {
    if (onSpotlightClose) onSpotlightClose();
    setInitialLoadOpen(true);
  }, [onSpotlightClose]);

  // Si el estado no se pudo cargar, la guia simplemente no se muestra: es una
  // ayuda, no puede dejar el Inicio inutilizable ni tapar el listado de
  // condominios con un error.
  if (!enabled || isLoading || error || !steps.length || isHidden) {
    return null;
  }

  const expandedKey = openKey ?? currentStep?.key ?? null;
  const welcomeExpanded = expandedKey === ONBOARDING_WELCOME_STEP.key;

  const toggleWelcome = () =>
    setOpenKey(welcomeExpanded ? "" : ONBOARDING_WELCOME_STEP.key);

  const handleDismiss = async (step) => {
    setBusyKey(step.key);
    try {
      await markDone(step.key);
    } catch (requestError) {
      handleErrorMessage(requestError);
    } finally {
      setBusyKey(null);
    }
  };

  const handleHide = async () => {
    // Se confirma porque ocultarla es permanente y queda guardado en el
    // condominio: un clic por error dejaba al usuario sin guia, y el camino de
    // vuelta esta en otra pantalla (Configuración del condominio), asi que hay
    // que decirle donde.
    const { isConfirmed } = await Swal.fire({
      title: "¿Ocultar la guía de primeros pasos?",
      html:
        "No va a volver a aparecer en el Inicio de este condominio." +
        "<br/><br/>Puedes volver a mostrarla cuando quieras desde " +
        "<strong>Configuración del condominio</strong>.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, ocultar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#950ec3",
    });

    if (!isConfirmed) {
      return;
    }

    setHiding(true);
    try {
      await setHidden(true);
    } catch (requestError) {
      handleErrorMessage(requestError);
    } finally {
      setHiding(false);
    }
  };

  const hideButton = (
    <Button
      color="link"
      size="sm"
      className="fs--2 text-600 p-0"
      disabled={hiding}
      onClick={handleHide}
    >
      {hiding ? "Ocultando..." : "Ocultar guía"}
    </Button>
  );

  return (
    <Card className="mb-3">
      <FalconCardHeader title="Primeros pasos" light={false}>
        <span className="fs--1 text-600 mr-3">
          {completed} de {total}
        </span>
        {hideButton}
      </FalconCardHeader>
      <CardBody className="pt-2">
        <Progress
          value={(completed / total) * 100}
          color="primary"
          className="mb-3"
          style={{ height: "6px" }}
        />

        <ul className="list-unstyled mb-0">
          {/* Bienvenida: paso fijo y solo frontend. No se completa ni se
              descarta, asi que vive fuera de `steps` (que se cruzan con el
              estado del backend) y siempre va primero. */}
          <li ref={welcomeRef} className="border-bottom py-2">
            <div
              role="button"
              tabIndex={0}
              className="d-flex align-items-center cursor-pointer"
              onClick={toggleWelcome}
              onKeyDown={(event) =>
                (event.key === "Enter" || event.key === " ") && toggleWelcome()
              }
            >
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 flex-shrink-0 fs--2 bg-soft-primary text-primary"
                style={{ width: "24px", height: "24px" }}
              >
                <FontAwesomeIcon icon={faHandshake} />
              </span>
              <span className="fs--1 mb-0 flex-grow-1 font-weight-semi-bold text-900">
                {ONBOARDING_WELCOME_STEP.title}
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="text-500 fs--2 ml-2 flex-shrink-0 transition-base"
                style={{
                  transform: welcomeExpanded ? "rotate(180deg)" : "none",
                }}
              />
            </div>

            <Collapse isOpen={welcomeExpanded}>
              <div className="pl-4 pt-2">
                <p className="fs--1 text-700 mb-2">
                  {ONBOARDING_WELCOME_STEP.description}
                </p>
                <div className="mt-2">
                  <ButtonIcon
                    color="primary"
                    size="sm"
                    icon={faCalendarAlt}
                    iconAlign="right"
                    tag="a"
                    href={ONBOARDING_WELCOME_STEP.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {ONBOARDING_WELCOME_STEP.cta}
                  </ButtonIcon>
                  {/* El mismo modal que "Cargar archivo" en Propietarios: aca
                      solo se acerca la carga al primer lugar que ve el
                      manager, para que no tenga que ir a buscarla. */}
                  <ButtonIcon
                    color="falcon-default"
                    size="sm"
                    icon={faFileExcel}
                    className="ml-sm-2 mt-2 mt-sm-0"
                    onClick={startInitialLoad}
                  >
                    {ONBOARDING_WELCOME_STEP.initialLoadCta}
                  </ButtonIcon>
                </div>
              </div>
            </Collapse>
          </li>

          {steps.map((step) => {
            const isExpanded = expandedKey === step.key;

            return (
              <li key={step.key} className="border-bottom py-2">
                <div
                  role="button"
                  tabIndex={0}
                  className="d-flex align-items-center cursor-pointer"
                  onClick={() => setOpenKey(isExpanded ? "" : step.key)}
                  onKeyDown={(event) =>
                    (event.key === "Enter" || event.key === " ") &&
                    setOpenKey(isExpanded ? "" : step.key)
                  }
                >
                  <span
                    className={`d-inline-flex align-items-center justify-content-center rounded-circle mr-2 flex-shrink-0 fs--2 ${
                      step.done
                        ? "bg-soft-success text-success"
                        : "bg-soft-primary text-primary"
                    }`}
                    style={{ width: "24px", height: "24px" }}
                  >
                    {step.done ? (
                      <FontAwesomeIcon icon={faCheck} />
                    ) : (
                      step.position
                    )}
                  </span>
                  <span
                    className={`fs--1 mb-0 flex-grow-1 ${
                      step.done ? "text-600" : "font-weight-semi-bold text-900"
                    }`}
                  >
                    {step.title}
                  </span>
                  {/* Sin esto no habia nada que dijera que la fila se abre:
                      el cursor de mano solo se ve cuando ya pasaste por
                      encima, y el detalle del paso quedaba escondido. */}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-500 fs--2 ml-2 flex-shrink-0 transition-base"
                    style={{
                      transform: isExpanded ? "rotate(180deg)" : "none",
                    }}
                  />
                </div>

                <Collapse isOpen={isExpanded}>
                  <div className="pl-4 pt-2">
                    <p className="fs--1 text-700 mb-2">{step.description}</p>
                    {step.key === "aliquot" && (
                      <AliquotHealthAlert
                        aliquot={aliquot}
                        onDistributed={refresh}
                        className="mt-2"
                      />
                    )}
                    <div className="mt-2">
                      <ButtonIcon
                        color={step.done ? "falcon-default" : "primary"}
                        size="sm"
                        icon={faArrowRight}
                        iconAlign="right"
                        onClick={() =>
                          history.push(step.to(selectedCondominium))
                        }
                      >
                        {step.cta}
                      </ButtonIcon>
                      {step.canDismiss && (
                        <Button
                          color="link"
                          size="sm"
                          className="ml-2"
                          disabled={busyKey === step.key}
                          onClick={() => handleDismiss(step)}
                        >
                          {busyKey === step.key
                            ? "Guardando..."
                            : step.dismissLabel}
                        </Button>
                      )}
                    </div>
                  </div>
                </Collapse>
              </li>
            );
          })}
        </ul>
      </CardBody>

      {/* Al guardar se recalcula el progreso: la carga inicial puede dejar
          completos los pasos de propietarios, alícuotas y saldos. */}
      <InitialLoadModal
        isOpen={initialLoadOpen}
        toggle={() => setInitialLoadOpen(false)}
        selectedCondominium={selectedCondominium}
        onSuccess={refresh}
      />

      <InitialLoadSpotlight
        isOpen={!!spotlightInitialLoad}
        targetRef={welcomeRef}
        onClose={onSpotlightClose || noop}
        onStart={startInitialLoad}
      />
    </Card>
  );
};

OnboardingChecklist.propTypes = {
  spotlightInitialLoad: PropTypes.bool,
  onSpotlightClose: PropTypes.func,
};

export default OnboardingChecklist;
